import {
  HEADERS,
  STATUS_NO_DATA,
  STATUS_OK,
  VERSION,
  getUrl,
} from './settings';

/**
 * Complete fetching helper for GET/POST against the AZmed data API.
 * Ported from azmonitor to keep feedback (and future calls) iso-functional.
 *
 * @param {string} endpoint Resource
 * @param {object} config Api call resources ({ action, body, ...customConfig })
 * @param {*} dispatch Unused (kept for signature parity)
 * @param {*} user Unused (kept for signature parity)
 * @param {string} mode [production || dev]
 * @param {AbortSignal} signal Optional abort signal
 * @param {string} version API version string
 * @returns {Promise<{data, headers, status, url}>} Request result
 */
export async function client(
  endpoint,
  { action, body, ...customConfig } = {},
  dispatch,
  user,
  mode = 'dev',
  signal,
  version = VERSION[1]
) {
  const URL = getUrl(mode, version);

  const config = {
    method: body ? 'POST' : 'GET',
    ...customConfig,
    headers: {
      ...HEADERS,
      ...(customConfig.headers || {}),
    },
    signal,
  };

  // Search of a body
  if (body) {
    config.body = JSON.stringify(body);
  }

  // Build the URL: {base}/{action}/{endpoint}[_{collection}]
  let completeUrl = `${URL}`;
  if (action) {
    completeUrl = `${completeUrl}/${action}`;
  }

  completeUrl = `${completeUrl}/${endpoint}`;
  if (customConfig.collection) {
    completeUrl = `${completeUrl}_${customConfig.collection}`;
  }

  // Data initialization
  let data;
  try {
    const response = await window.fetch(`${completeUrl}`, config);
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (response.ok) {
      if (response.status === STATUS_OK) {
        return {
          data,
          headers: response.headers,
          status: response.status,
          url: response.url,
        };
      }

      return {
        data: {},
        headers: response.headers,
        status: response.status,
        url: response.url,
      };
    }

    if (response.status === STATUS_NO_DATA) {
      return {
        data: {},
        headers: response.headers,
        status: response.status,
        url: response.url,
      };
    }

    if (response.status === 404) {
      throw new Error('Not found');
    }

    throw new Error(response.error || response.statusText);
  } catch (err) {
    return Promise.reject(
      data && data.message
        ? data.message
        : data && data.error
        ? data.error
        : err
    );
  }
}

// Get function
client.get = function(
  endpoint,
  customConfig = {},
  user,
  dispatch,
  mode = 'dev',
  signal,
  version
) {
  return client(
    endpoint,
    { ...customConfig, method: 'GET' },
    dispatch,
    user,
    mode,
    signal,
    version
  );
};

// Post function
client.post = function(
  endpoint,
  body,
  customConfig = {},
  user,
  dispatch,
  mode = 'dev',
  signal,
  version
) {
  return client(
    endpoint,
    { ...customConfig, body },
    dispatch,
    user,
    mode,
    signal,
    version
  );
};
